import React from "react";
import { Play, Lock, FileText, DollarSign, RotateCcw, Send } from "lucide-react";
import { toast } from "../../../ui/Toast";

const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

// Wrap a parent-supplied action with toast feedback. Parent callbacks
// throw on failure (see AdminPayrollPage.runAction).
const wrap = (fn, { info, success, error }) => async () => {
  if (info) toast.info(info);
  try {
    await fn?.();
    toast.success(success);
  } catch (_) {
    toast.error(error);
  }
};

const STATUS_STYLE = {
  DRAFT:    "text-white/50 bg-white/5 border-white/10",
  REVIEW:   "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  LOCKED:   "text-blue-400 bg-blue-500/10 border-blue-500/20",
  APPROVED: "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  PAID:     "text-purple-400 bg-purple-500/10 border-purple-500/20",
};

export default function RunActionsBar({ status = "DRAFT", onApprove, onLock, onGenerate, onDisburse, onReopen, onRecalc, loading }) {
  const statusStyle = STATUS_STYLE[status] || STATUS_STYLE.DRAFT;

  const handleRecalc   = wrap(onRecalc,   { info: "Recalculating…",       success: "Payroll recalculated",  error: "Recalc failed"               });
  const handleApprove  = wrap(onApprove,  {                                success: "Payroll run approved",  error: "Failed to approve run"        });
  const handleLock     = wrap(onLock,     {                                success: "Timesheets locked",     error: "Failed to lock"              });
  const handleGenerate = wrap(onGenerate, {                                success: "Payslips generated",    error: "Failed to generate payslips" });

  return (
    <div className={`${card} px-5 py-4 flex flex-wrap items-center gap-3`}>
      <div className="flex items-center gap-2 mr-2">
        <span className="text-xs text-white/30 uppercase tracking-wider">Run Status</span>
        <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${statusStyle}`}>{status}</span>
      </div>

      <div className="flex flex-wrap gap-2 ml-auto">
        {onRecalc && (
          <button onClick={handleRecalc} disabled={loading}
            className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-white/10 bg-white/5 text-white/50 hover:bg-white/10 hover:text-white disabled:opacity-40 transition-colors">
            <RotateCcw size={12} /> Recalculate
          </button>
        )}

        {status === "DRAFT" && onApprove && (
          <button onClick={handleApprove} disabled={loading}
            className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg border border-yellow-500/30 bg-yellow-500/10 text-yellow-400/80 hover:bg-yellow-500/20 hover:text-yellow-400 disabled:opacity-40 transition-colors">
            <Send size={12} /> Send for Review
          </button>
        )}

        {status === "REVIEW" && onLock && (
          <button onClick={handleLock} disabled={loading}
            className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg border border-blue-500/30 bg-blue-500/10 text-blue-400/80 hover:bg-blue-500/20 hover:text-blue-400 disabled:opacity-40 transition-colors">
            <Lock size={12} /> Lock Run
          </button>
        )}

        {status === "LOCKED" && (
          <>
            {onGenerate && (
              <button onClick={handleGenerate} disabled={loading}
                className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] disabled:opacity-40 transition-colors">
                <FileText size={12} /> Generate Payslips
              </button>
            )}
            {onDisburse && (
              // Disburse endpoint isn't built yet — render disabled with
              // a "Coming soon" hint so the workflow doesn't 404.
              <button disabled
                title="Bank disbursement integration coming soon"
                className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg border border-white/10 bg-white/5 text-white/30 cursor-not-allowed">
                <DollarSign size={12} /> Mark as Paid <span className="ml-1 text-[9px] uppercase tracking-widest text-yellow-300/60">soon</span>
              </button>
            )}
          </>
        )}

        {status === "PAID" && onReopen && (
          // Reopen endpoint isn't built yet — render disabled.
          <button disabled
            title="Reopen a paid run — coming soon"
            className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-white/10 bg-white/5 text-white/30 cursor-not-allowed">
            <RotateCcw size={12} /> Reopen Run <span className="ml-1 text-[9px] uppercase tracking-widest text-yellow-300/60">soon</span>
          </button>
        )}
      </div>
    </div>
  );
}